import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import CustomConfirmation from '../../Common/CustomConfirmation';

import go_n from '../../Common/Images/go_n.gif';
import go_o from '../../Common/Images/go_o.gif';

import back_n from '../../Common/Images/back_n.gif';
import back_o from '../../Common/Images/back_o.gif';

import continue_n from '../../Common/Images/continue_n.gif';
import continue_o from '../../Common/Images/continue_o.gif';

import delete_n from '../../Common/Images/delete_n.gif';
import delete_o from '../../Common/Images/delete_o.gif';

import edit_n from '../../Common/Images/edit_n.gif';
import edit_o from '../../Common/Images/edit_o.gif';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface WorkItem {
    WorkID: number;
    WorkTitle: string;
    StartDate: string;
    FinishDate: string;
    Subject: string;
    AssignedTo: string;
    ActivityList: string[];
    RawActivities: string;
    DicCode: string;
    CanDelete: boolean;
    ToWhomCode: number;
    FamilyCode: string;
    StudentLogin: string;
    Level: string;
    GradeYear: string | number;
}

interface ReportRow {
    Login: string;
    Name: string;
    Scores: { [key: string]: { value: string | number, color: string } };
}

interface ActivityCol {
    code: string;
    name: string;
}

interface DrillDownItem {
    Activity: string;
    TimesDone: number | string;
    LastDone: string;
    Score: number;
    TotalQs: number | string;
    Correct: number | string;
    AnsByHelp: number | string;
    WrongAns: number | string;
    Skipped: number | string;
    SolutionSeen: number | string;
    Remarks: string;
}

const EL_ViewWork: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const schoolCode = searchParams.get('SchoolCode') || 'DLR_KE_AU';
    const teacherLogin = searchParams.get('TeacherLogin') || 'Tutor';
    const gradeYear = searchParams.get('GradeYear') || '';
    const classCode = searchParams.get('ClassCode') || '';

    const [screen, setScreen] = useState<1 | 2 | 3>(1);
    const [loading, setLoading] = useState(false);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    const [confirmInfo, setConfirmInfo] = useState<{
        show: boolean;
        message: string;
        onConfirm: () => void;
    }>({ show: false, message: '', onConfirm: () => { } });

    const [hoverStates, setHoverStates] = useState({
        go: false,
        back: false,
        continue: false,
        edit: false,
        delete: false
    });

    const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [works, setWorks] = useState<WorkItem[]>([]);
    const [selectedWorkId, setSelectedWorkId] = useState<number | null>(null);

    const [selectedWork, setSelectedWork] = useState<WorkItem | null>(null);
    const [reportRows, setReportRows] = useState<ReportRow[]>([]);
    const [reportCols, setReportCols] = useState<ActivityCol[]>([]);

    const [drillDetails, setDrillDetails] = useState<DrillDownItem[]>([]);
    const [selectedStudentName, setSelectedStudentName] = useState('');

    const setHover = (btn: keyof typeof hoverStates, isHover: boolean) => {
        setHoverStates(prev => ({ ...prev, [btn]: isHover }));
    };

    const urlStudentName = searchParams.get('StudentName') || '';

    const fetchWorks = async () => {
        setLoading(true);
        try {
            const studentLoginParam = searchParams.get('Work2Login') || '';
            const familyCodeParam = searchParams.get('Work2FamilyCode') || '';

            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                TeacherLogin: teacherLogin,
                StartDate: startDate,
                EndDate: endDate,
                GradeYear: gradeYear,
                ClassCode: classCode,
                Work2Login: studentLoginParam,
                Work2FamilyCode: familyCodeParam
            });

            const response = await fetch(`${API_BASE_URL}api/teacher/get_works/?${params.toString()}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();

            if (response.ok) {
                if (data.works) {
                    setWorks(data.works);
                } else {
                    setWorks(Array.isArray(data) ? data : []);
                }

                if (data.studentName) {
                    setSelectedStudentName(data.studentName);
                }

                setSelectedWorkId(null);
            } else {
                setAlertInfo({ show: true, message: data.message || "Failed to load works." });
            }
        } catch (error) {
            console.error(error);
            setAlertInfo({ show: true, message: "Network error." });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (schoolCode && teacherLogin) fetchWorks();
    }, [schoolCode, teacherLogin]);

    const handleRowClick = (workId: number) => {
        setSelectedWorkId(workId);
    };

    const handleContinue = async () => {
        if (screen === 1) {
            if (!selectedWorkId) {
                setAlertInfo({ show: true, message: "	Please select a work" });
                return;
            }
            const work = works.find(w => w.WorkID === selectedWorkId);
            if (!work) return;

            if (work.ToWhomCode === 4) {
                setLoading(true);
                setSelectedWork(work);
                try {
                    const params = new URLSearchParams({
                        SchoolCode: schoolCode,
                        WorkID: work.WorkID.toString(),
                        Mode: 'DrillDown'
                    });

                    const response = await fetch(`${API_BASE_URL}api/teacher/get_work_report/?${params.toString()}`, {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' }
                    });

                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setDrillDetails(data);
                        setScreen(3);
                    } else {
                        setAlertInfo({ show: true, message: "No data found." });
                    }
                } catch (error) {
                    console.error(error);
                    setAlertInfo({ show: true, message: "Error loading report" });
                } finally {
                    setLoading(false);
                }
            } else {
                handleViewReport(work);
            }
        }
    };

    // const handleExit = (e: React.MouseEvent) => {
    //     e.preventDefault();

    //     // if (location.state?.returnPath) {
    //     //     navigate(location.state.returnPath);
    //     //     return;
    //     // }

    //     const family = searchParams.get('Work2FamilyCode') || '';
    //     const login = searchParams.get('Work2Login') || '';

    //     const params = new URLSearchParams({
    //         FilterType: 'CustEntry',
    //         SchoolCode: schoolCode,
    //         FamilyCode: family,
    //         Login: login,
    //     });

    //     // http://localhost:3000/SUPPORT/EL_AddEditTask?FilterType=CustEntry&FamilyCode=A&SchoolCode=DLR_KE_AU&Login=vtlt&S=A&F=AA&L=SHUBHANGA

    //     navigate(`/SUPPORT/EL_AddEditTask?FilterType=CustEntry&${`FamilyCode=${sessionStorage.getItem('S')}&SchoolCode=${schoolCode}&Login=${family}&S=${sessionStorage.getItem('S')}&F=${sessionStorage.getItem('F')}&L=${sessionStorage.getItem('L')}`}`)
    //     // , {
    //     //     state: {
    //     //         returnPath: `/SCHOOL/Teacher/EL_ViewWork?${params.toString()}`,
    //     //         parentReturnPath: location.state?.parentReturnPath || null
    //     //     }
    //     // });
    // };

const handleExit = (e: React.MouseEvent) => {
    e.preventDefault();

    const returnPathFromState = location.state?.returnPath;
    const returnPathFromParams = searchParams.get('ReturnPath');
    const finalReturnPath = returnPathFromState || returnPathFromParams;
    
    if (finalReturnPath) {
        navigate(finalReturnPath, {
            replace: true
        });
        return;
    }

    const family = searchParams.get('Work2FamilyCode') || '';
    const login = searchParams.get('Work2Login') || '';

    navigate(
        `/SUPPORT/EL_AddEditTask?FilterType=CustEntry&FamilyCode=${sessionStorage.getItem("S")}&SchoolCode=${schoolCode}&Login=${sessionStorage.getItem('AddEditLogin')}&S=${sessionStorage.getItem("S")}&F=${sessionStorage.getItem("F")}&L=${sessionStorage.getItem("L")}`,
        {
            replace: true
        }
    );
};

    const handleActivityDrillDown = async (activityCode: string, activityName: string) => {
        if (!selectedWork) return;
        setLoading(true);
        setSelectedStudentName(`Activity: ${activityName}`);

        try {
            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                WorkID: selectedWork.WorkID.toString(),
                Mode: 'DrillDown',
                ActivityCode: activityCode
            });

            const response = await fetch(`${API_BASE_URL}api/teacher/get_work_report/?${params.toString()}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();
            if (Array.isArray(data)) {
                setDrillDetails(data);
                setScreen(3);
            } else {
                setAlertInfo({ show: true, message: "No data found for this activity." });
            }
        } catch (error) {
            console.error(error);
            setAlertInfo({ show: true, message: "Error loading activity report" });
        } finally {
            setLoading(false);
        }
    };

    const handleViewReport = async (work: WorkItem) => {
        setLoading(true);
        setSelectedWork(work);
        try {
            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                WorkID: work.WorkID.toString(),
                Mode: 'StudentWise'
            });

            const response = await fetch(`${API_BASE_URL}api/teacher/get_work_report/?${params.toString()}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();
            if (data.rows) {
                setReportCols(data.columns);
                setReportRows(data.rows);
                setScreen(2);
            }
        } catch (error) {
            console.error(error);
            setAlertInfo({ show: true, message: "Error loading report" });
        } finally {
            setLoading(false);
        }
    };

    const handleDrillDown = async (studentLogin: string, studentName: string) => {
        if (!selectedWork) return;
        setLoading(true);
        setSelectedStudentName(studentName);
        try {
            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                WorkID: selectedWork.WorkID.toString(),
                Mode: 'DrillDown',
                DrillLogin: studentLogin
            });

            const response = await fetch(`${API_BASE_URL}api/teacher/get_work_report/?${params.toString()}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();
            if (Array.isArray(data)) {
                setDrillDetails(data);
                setScreen(3);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        if (!selectedWorkId) {
            setAlertInfo({ show: true, message: "Please select a record to delete." });
            return;
        }

        setConfirmInfo({
            show: true,
            message: "Are you sure you want to delete this work?",
            onConfirm: async () => {
                setConfirmInfo({ show: false, message: '', onConfirm: () => { } }); // Close dialog immediately
                setLoading(true);
                try {
                    const response = await fetch(`${API_BASE_URL}api/teacher/delete_work/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            SchoolCode: schoolCode,
                            WorkID: selectedWorkId
                        })
                    });

                    const result = await response.json();

                    if (result.status === 'success') {
                        setAlertInfo({ show: true, message: "Work deleted successfully." });
                        setSelectedWorkId(null);
                        fetchWorks();
                    } else {
                        setAlertInfo({ show: true, message: result.message || "Failed to delete work." });
                    }
                } catch (error) {
                    console.error(error);
                    setAlertInfo({ show: true, message: "Network error occurred while deleting." });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleEdit = () => {
        if (!selectedWorkId) {
            setAlertInfo({ show: true, message: "Please select a record to edit." });
            return;
        }

        const work = works.find(w => w.WorkID === selectedWorkId);
        if (!work) return;

        navigate({
            pathname: '/SCHOOL/Teacher/EL_SetWork',
            search: `?SchoolCode=${schoolCode}` +
                `&WorkID=${work.WorkID}` +
                `&GradeYear=${work.GradeYear || searchParams.get('GradeYear') || ''}` +
                `&StudentName=${encodeURIComponent(work.AssignedTo)}` +
                `&Work2FamilyCode=${work.FamilyCode || ''}` +
                `&Work2Login=${work.StudentLogin || ''}`
        }, {
            state: {
                returnPath: null,
                parentReturnPath: location.state?.returnPath
            }
        });
    };

    const styles = {
        container: {
            fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
            fontSize: '8pt',
            color: '#000000',
            width: '100%',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column' as 'column',
            backgroundColor: '#FFFFFF',
            padding: '10px 10px 0 10px',
            boxSizing: 'border-box' as 'border-box'
        },
        scrollableContent: {
            flex: 1,
            overflowY: 'auto' as 'auto',
            border: '1px solid #7F9DB9',
            marginBottom: '0px'
        },
        table: {
            borderCollapse: 'collapse' as const,
            backgroundColor: 'white',
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontSize: '8pt',
            width: '100%'
        },
        th: {
            backgroundColor: '#C7DDFD',
            color: '#2859ac',
            border: '1px solid #80a0ff',
            padding: '4px 6px',
            fontSize: '10pt',
            height: '22px',
            textAlign: 'left' as const,
            fontWeight: 'normal',
            position: 'sticky' as const,
            top: 0,
            zIndex: 1,

            whiteSpace: 'nowrap' as const,
        },
        td: {

            borderLeft: 'solid 1px #a3c0e8',
            padding: '8px 6px',
            fontSize: '10pt',
            lineHeight: '16px',
            verticalAlign: 'top' as const,
            whiteSpace: 'nowrap' as const,
            width: 'auto'
        },
        filterBox: {
            border: '1px solid #80a0ff',
            padding: '5px 3px 5px 10px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '11pt',
            backgroundColor: 'white'
        },
        inputDate: {
            fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
            fontSize: '11pt',
            border: '1px solid #80a0ff',
            padding: '2px',
            color: 'blue',
        },
        footerTable: {
            width: '100%',
            borderCollapse: 'collapse' as 'collapse',
            marginTop: 'auto',
            border: '0px'
        },
        footerBar: {
            backgroundColor: '#548BED',
            borderTop: '1px solid #80a0ff',
            padding: '5px 20px',
            height: '40px',
            verticalAlign: 'middle' as 'middle'
        },
        footerFlex: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
        },
        actionGroup: {
            display: 'flex',
            gap: '10px',
            marginLeft: 'auto'
        }
    };

    return (
        <div style={styles.container}>
            {loading && <Loading />}

            <table id='tblHeader1' style={{ width: '100%', border: '0' }} cellPadding={0} cellSpacing={0}>
                <tbody>
                    <tr style={{ backgroundColor: '#E4E4E4' }}>
                        <td className='lableStyle' style={{ height: '26px', paddingLeft: '5px', verticalAlign: 'middle', fontFamily: 'Arial', fontSize: '13px', color: '#548BED', alignItems: 'left' }}>
                            eDealer: Kinetic Education | Tutor:
                        </td>
                        <td className='lableStyle' style={{ height: '26px', paddingRight: '15px', verticalAlign: 'middle', fontFamily: 'Arial', fontSize: '13px', color: '#548BED', textAlign: 'right' }}>
                            &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;
                            <a href="#" id='lnkLogout' onClick={handleExit}>Cancel</a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table id='tblHeader2' style={{ width: '100%', border: '0', borderCollapse: 'collapse', marginTop: '3px', marginBottom: '15px' }}>
                <tbody>
                    <tr style={{ backgroundColor: '#E4E4E4', fontFamily: 'Arial', fontSize: '16px' }}>
                        <td className='PageHeader' style={{ height: '26px', verticalAlign: 'middle', paddingLeft: '5px', }}>
                            Work set for {selectedStudentName || urlStudentName}
                        </td>
                        <td style={{ height: '26px', verticalAlign: 'middle', textAlign: 'right', paddingRight: '15px' }}></td>
                    </tr>
                </tbody>
            </table>

            {screen === 1 && (
                <div style={styles.filterBox}>
                    <span>Finish date in between</span>
                    <input
                        type="date"
                        style={styles.inputDate}
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                    />
                    <span>and</span>
                    <input
                        type="date"
                        style={styles.inputDate}
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                    />
                    <img
                        src={hoverStates.go ? go_o : go_n}
                        alt="Go"
                        onMouseEnter={() => setHover('go', true)}
                        onMouseLeave={() => setHover('go', false)}
                        onClick={fetchWorks}
                        style={{ cursor: 'pointer', verticalAlign: 'middle', marginLeft: '10px' }}
                    />
                </div>
            )}

            {(screen === 2 || screen === 3) && selectedWork && (
                <div style={{ marginBottom: '10px', fontSize: '11pt', paddingLeft: '10px', color: 'blue', fontWeight: 'bold'}}>
                    <span style={{ color: 'black', fontWeight: 'normal', fontSize: "11pt" }}>Work Title:</span> {selectedWork.WorkTitle}
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <span style={{ color: 'black', fontWeight: 'normal', fontSize: "11pt" }}>Start Date:</span> {selectedWork.StartDate}
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <span style={{ color: 'black', fontWeight: 'normal', fontSize: "11pt" }}>Finish Date:</span> {selectedWork.FinishDate}
                </div>
            )}

            <div style={styles.scrollableContent}>
                {screen === 1 && (
                    <table style={styles.table}>
                        <colgroup>
                            <col style={{ width: "35px" }} />
                            <col style={{ width: "170px" }} />
                            <col style={{ width: "280px" }} />
                            <col style={{ width: "120px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "60px" }} />
                            <col />
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, textAlign: 'center' }}>#</th>
                                <th style={styles.th}>Work Title</th>
                                <th style={styles.th}>Assigned To</th>
                                <th style={styles.th}>Subject</th>
                                <th style={styles.th}>Start Date</th>
                                <th style={styles.th}>Finish Date</th>
                                <th style={styles.th}>Level</th>
                                <th style={styles.th}>Activities</th>
                            </tr>
                        </thead>
                        <tbody>
                            {works.map((w, i) => {
                                const isSelected = selectedWorkId === w.WorkID;
                                const rowBg = isSelected ? 'orange' : (i % 2 === 0 ? '#F4FAFF' : 'white');
                                const rowColor = isSelected ? '#FFFFFF' : '#333333';

                                const isLastRow = i === works.length - 1;

                                return (
                                    <tr
                                        key={w.WorkID}
                                        onClick={() => handleRowClick(w.WorkID)}
                                        style={{
                                            backgroundColor: rowBg,
                                            color: rowColor,
                                            cursor: 'pointer',
                                            borderBottom: isLastRow ? '1px solid #7F9DB9' : 'none',
                                            padding:'6px'
                                        }}
                                    >
                                        <td style={{ ...styles.td, textAlign: 'center', color: rowColor }}>
                                            {i + 1}
                                        </td>
                                        <td style={{ ...styles.td, color: rowColor }}>
                                            <span
                                                style={{
                                                    color: isSelected ? '#FFFFFF' : 'black',
                                                }}
                                            >
                                                {w.WorkTitle}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, color: rowColor }}>{w.AssignedTo}</td>
                                        <td style={{ ...styles.td, color: rowColor }}>{w.Subject}</td>
                                        <td style={{ ...styles.td, color: rowColor }}>{w.StartDate}</td>
                                        <td style={{ ...styles.td, color: rowColor }}>{w.FinishDate}</td>

                                        <td style={{ ...styles.td, color: rowColor }}>
                                            {w.Level}
                                        </td>
                                        <td style={{ ...styles.td, color: rowColor }}>
                                            {w.ActivityList.map((act, idx) => (
                                                <React.Fragment key={idx}>
                                                    {act}
                                                    <br />
                                                </React.Fragment>
                                            ))}
                                        </td>
                                    </tr>
                                );
                            })}
                            {works.length === 0 && (
                                <tr><td colSpan={8} style={{ ...styles.td, textAlign: 'center', padding: '20px' }}></td></tr>
                            )}
                        </tbody>
                    </table>
                )}

                {screen === 2 && (
                    <thead>
                        <tr>
                            <th style={styles.th}>Student Name</th>
                            {reportCols.map(c => (
                                <th
                                    key={c.code}
                                    style={{
                                        ...styles.th,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        color: '#005EC4',
                                        textDecoration: 'underline'
                                    }}
                                    onClick={() => handleActivityDrillDown(c.code, c.name)}
                                >
                                    {c.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}

                {screen === 3 && (
                    <table style={styles.table}>

                        <colgroup>
                            <col style={{ width: "35px" }} />
                            <col style={{ width: "220px" }} />
                            <col style={{ width: "80px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "60px" }} />
                            <col style={{ width: "80px" }} />
                            <col style={{ width: "80px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "80px" }} />
                            <col style={{ width: "80px" }} />
                            <col style={{ width: "90px" }} />
                            <col />
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, textAlign: 'center' }}>#</th>
                                <th style={styles.th}>Activity</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Times<br />Done</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Last<br />Done On</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Score</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Total<br />Questions</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Correct<br />Answers</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Answered<br />By Help</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Wrong<br />Answers</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Questions<br />Skipped</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Solution<br />fully seen</th>
                                <th style={styles.th}>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drillDetails.map((d, i) => {
                                const isLastRow = i === drillDetails.length - 1;
                                return (
                                    <tr
                                        key={i}
                                        style={{
                                            backgroundColor: i % 2 === 0 ? '#F4FAFF' : 'white',
                                            borderBottom: isLastRow ? '1px solid #7F9DB9' : 'none',
                                            padding:"8px"
                                        }}
                                    >
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{i + 1}</td>
                                        <td style={{ ...styles.td, color: '#333' }}>{d.Activity}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.TimesDone}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.LastDone}</td>
                                        <td style={{
                                            ...styles.td,
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            color: d.Score >= 90 ? 'green' : d.Score < 80 ? 'red' : 'orange'
                                        }}>
                                            {d.Score}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.TotalQs}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.Correct}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.AnsByHelp}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.WrongAns}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.Skipped}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#333' }}>{d.SolutionSeen}</td>
                                        <td style={{ ...styles.td, color: '#333' }}>{d.Remarks}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <table style={styles.footerTable}>
                <tbody>
                    <tr>
                        <td style={styles.footerBar}>
                            <div style={styles.footerFlex}>

                                <div>
                                    {screen > 1 && (
                                        <img
                                            src={hoverStates.back ? back_o : back_n}
                                            alt="Back"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={() => setHover('back', true)}
                                            onMouseLeave={() => setHover('back', false)}
                                            onClick={() => {
                                                if (screen === 3 && selectedWork?.ToWhomCode === 4) {
                                                    setScreen(1);
                                                    setSelectedWorkId(null);
                                                } else {
                                                    setScreen((screen - 1) as 1 | 2 | 3);
                                                }
                                            }}
                                        />
                                    )}
                                </div>

                                <div style={styles.actionGroup}>
                                    {screen === 1 && (
                                        <>
                                            <img
                                                src={hoverStates.delete ? delete_o : delete_n}
                                                alt="Delete"
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={() => setHover('delete', true)}
                                                onMouseLeave={() => setHover('delete', false)}
                                                onClick={handleDelete}
                                            />
                                            <img
                                                src={hoverStates.edit ? edit_o : edit_n}
                                                alt="Edit"
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={() => setHover('edit', true)}
                                                onMouseLeave={() => setHover('edit', false)}
                                                onClick={handleEdit}
                                            />

                                            <img
                                                src={hoverStates.continue ? continue_o : continue_n}
                                                alt="Continue"
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={() => setHover('continue', true)}
                                                onMouseLeave={() => setHover('continue', false)}
                                                onClick={handleContinue}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {confirmInfo.show && (
                <CustomConfirmation
                    message={confirmInfo.message}
                    onConfirm={confirmInfo.onConfirm}
                    onClose={() => setConfirmInfo({ ...confirmInfo, show: false })}
                />
            )}

            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}
        </div>
    );
};

export default EL_ViewWork;